/**
 * TableLifecycleHeaderActions — Designer-side ([Variant 2](Variant 2 / Table
 * Designer)) trigger cluster for rename + move-to-schema. Renders two
 * popover triggers next to the table editor header.
 *
 * The two popovers consume the same `TableLifecycleFormBody` mounted by
 * Variant 1 (Query Editor inline). When the structure isn't a `create-table`
 * / `alter-table` arm (e.g. Designer is parked on a CREATE INDEX buffer),
 * `fromTable` falls back to a synthetic empty QualifiedName — submission
 * surfaces the validator's required-field error instead of blowing up.
 *
 * Engine gating mirrors the inline popover: mysql / oracle / sqlite see a
 * disabled `[Move to schema]` trigger with the documented redirect tooltip.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D1 — Variant 2 (Table Designer).
 */
import { useState } from 'react';
import { ArrowRightLeft, FileEdit } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import type { QualifiedName } from '../../../../sql-grammar/model';
import {
  getMoveTableToSchemaUnsupportedTooltip,
  isMoveTableToSchemaSupported,
} from '../../edit/edits/moveTableToSchema';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { VqeTooltip } from '../../../visual-query-editor/render/common/VqeTooltip';
import { TableLifecycleFormBody } from '../common/TableLifecycleFormBody';
import type { TableLifecycleMode } from '../common/TableLifecycleFormBody';

/** Icon-only (compact) triggers name themselves in a real tooltip — a native
 *  `title` never showed reliably, so the two glyphs read as unlabelled. */
const ICON_TOOLTIPS: Record<TableLifecycleMode, string> = {
  rename: 'Rename table',
  'move-schema': 'Move table to another schema',
};

const FALLBACK_FROM_TABLE: QualifiedName = {
  name: '',
  raw: '',
  quoted: false,
  foldedName: '',
};

export interface TableLifecycleHeaderActionsProps {
  readonly editContext: VqeDdlEditContext;
  readonly fromTable: QualifiedName | null;
  readonly testId: string;
  /**
   * Compact tab-strip mode: icon-only triggers (label rides the tooltip +
   * aria-label) so both actions fit beside the four tabs at the popover's
   * default width instead of wrapping onto their own row.
   */
  readonly iconOnly?: boolean;
}

export function TableLifecycleHeaderActions({
  editContext,
  fromTable,
  testId,
  iconOnly = false,
}: TableLifecycleHeaderActionsProps) {
  return (
    <>
      <LifecycleTrigger
        editContext={editContext}
        mode="rename"
        fromTable={fromTable ?? FALLBACK_FROM_TABLE}
        testId={`${testId}-rename`}
        iconOnly={iconOnly}
      />
      <LifecycleTrigger
        editContext={editContext}
        mode="move-schema"
        fromTable={fromTable ?? FALLBACK_FROM_TABLE}
        testId={`${testId}-move-schema`}
        iconOnly={iconOnly}
      />
    </>
  );
}

interface LifecycleTriggerProps {
  readonly editContext: VqeDdlEditContext;
  readonly mode: TableLifecycleMode;
  readonly fromTable: QualifiedName;
  readonly testId: string;
  readonly iconOnly: boolean;
}

function LifecycleTrigger({
  editContext,
  mode,
  fromTable,
  testId,
  iconOnly,
}: LifecycleTriggerProps) {
  const [open, setOpen] = useState(false);
  const engine = editContext.service.engineId;
  const Icon = mode === 'rename' ? FileEdit : ArrowRightLeft;
  const label = mode === 'rename' ? 'Rename' : 'Move to schema';

  if (mode === 'move-schema' && !isMoveTableToSchemaSupported(engine)) {
    // The explanation rides on a `VqeTooltip` wrapper, not the `title=` alone:
    // shadcn's disabled Button is `pointer-events-none`, so a native title
    // tooltip can never surface. `title` stays as the machine-readable mirror
    // (asserted by unit + E2E specs).
    const reason = getMoveTableToSchemaUnsupportedTooltip(engine);
    return (
      <VqeTooltip content={reason} selfProvider>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={
            iconOnly
              ? 'h-7 w-7 p-0 text-muted-foreground'
              : 'h-7 gap-1 px-2 text-xs text-muted-foreground'
          }
          disabled
          aria-disabled
          aria-label={label}
          title={reason}
          data-testid={testId}
        >
          <Icon className="h-3.5 w-3.5" />
          {iconOnly ? null : label}
        </Button>
      </VqeTooltip>
    );
  }

  const trigger = (
    <PopoverTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={iconOnly ? 'h-7 w-7 p-0' : 'h-7 gap-1 px-2 text-xs'}
        aria-label={label}
        data-testid={testId}
      >
        <Icon className="h-3.5 w-3.5" />
        {iconOnly ? null : label}
      </Button>
    </PopoverTrigger>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* The tooltip wraps the popover trigger (never the other way round), so
          the Button stays the popover's own trigger. */}
      {iconOnly ? (
        <VqeTooltip content={ICON_TOOLTIPS[mode]} selfProvider>
          {trigger}
        </VqeTooltip>
      ) : (
        trigger
      )}
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="w-72"
        data-testid={`${testId}-popover`}
      >
        <TableLifecycleFormBody
          editContext={editContext}
          mode={mode}
          fromTable={fromTable}
          testId={`${testId}-form`}
          onCommitted={() => setOpen(false)}
          onCancel={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}
